import { SplashScreen } from "@capacitor/splash-screen";
import { setUser } from "@sentry/react";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";

import { setAuthToken, setImpersonatingClient, setImpersonatingExpert } from "@actions/sessionActions";
import { sessionContext } from "@context/Contexts";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import { GetMeDocument, GetMeQuery, GetMeQueryVariables } from "@typing/Generated";
import storage from "@utils/storageUtils";

type Props = { children: ReactNode };

const BootupProvider = ({ children }: Props) => {
  const { sessionState } = useContext(sessionContext);

  const { dispatch } = useContext(sessionContext);
  const initializingAuth = useRef<boolean>(true);
  const booting = useRef<boolean>(true);
  const movingStorageKeys = useRef<boolean>(true);
  const [, setForceRender] = useState<number>(0);
  const [forceInitializeAuth, setForceInitializeAuth] = useState<number>(0);

  useEffect(() => {
    if (sessionState.me) {
      const user = sessionState.me;
      if (user) {
        setUser({
          email: user.email.email,
          id: user.id,
          name: user.fullName,
          type: user.__typename
        });
      }
    }
  }, [sessionState.me]);

  useEffect(() => {
    const moveKeys = [
      "AUTH_TOKEN",
      "IMPERSONATE_CLIENT_AUTH_TOKEN",
      "IMPERSONATE_EXPERT_AUTH_TOKEN",
      "NEXT_CONSULT_LAST_REMINDED",
      "ACCESS_TYPE",
      "GOAL_INTRODUCTION_DISMISSED",
      "groupMessagingWarned"
    ];
    const movePromises: Promise<void>[] = [];
    moveKeys.forEach(key => {
      const oldValue = localStorage.getItem(key);
      if (oldValue) {
        localStorage.removeItem(key);
        const promise = new Promise<void>((resolve, _reject) => {
          storage.get(key).then(newStorageValue => {
            if (newStorageValue) {
              resolve();
            } else {
              storage.set(key, oldValue).then(() => resolve());
            }
          });
        });
        movePromises.push(promise);
      }
    });
    if (movePromises.length > 0) {
      Promise.all(movePromises).then(() => {
        movingStorageKeys.current = false;
        setForceInitializeAuth(Math.random());
      });
    } else {
      movingStorageKeys.current = false;
      setForceInitializeAuth(Math.random());
    }
  }, []);

  const getMe = useLazyQueryWithPromise<GetMeQuery, GetMeQueryVariables>(GetMeDocument);

  useEffect(() => {
    const interval = setInterval(() => {
      if (initializingAuth.current === false && movingStorageKeys.current === false) {
        SplashScreen.hide();
        booting.current = false;
        clearInterval(interval);
        setForceRender(Math.random());
      } else {
        setForceInitializeAuth(Math.random());
        setForceRender(Math.random());
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (movingStorageKeys.current === true) {
      return;
    }

    const bootFromStorage = async () => {
      const token = await storage.get("AUTH_TOKEN");
      const clientToken = await storage.get("IMPERSONATE_CLIENT_AUTH_TOKEN");
      const expertToken = await storage.get("IMPERSONATE_EXPERT_AUTH_TOKEN");

      if (token) {
        getMe({ context: { authToken: token }, fetchPolicy: "network-only" })
          .then(response => {
            if (response.data.me) {
              dispatch(setAuthToken(token, response.data.me));
            }

            if (clientToken) {
              getMe({ context: { authToken: clientToken }, fetchPolicy: "network-only" }).then(response => {
                if (response.data.me) {
                  dispatch(setImpersonatingClient(clientToken, response.data.me));
                }
                initializingAuth.current = false;
              });
            }

            if (expertToken) {
              getMe({ context: { authToken: expertToken }, fetchPolicy: "network-only" }).then(response => {
                if (response.data.me) {
                  dispatch(setImpersonatingExpert(expertToken, response.data.me));
                }
                initializingAuth.current = false;
              });
            }

            if (!clientToken && !expertToken) {
              initializingAuth.current = false;
            }
          })
          .catch(() => {
            initializingAuth.current = false;
          });
      } else {
        initializingAuth.current = false;
      }
    };
    bootFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceInitializeAuth]);

  if (booting.current) {
    return null;
  }

  /* eslint-disable-next-line */
  return <>{children}</>;
};

export default BootupProvider;
